"use client";

import { useState } from "react";
import Image from "next/image";
import { MoreVertical } from "lucide-react";
import { DeviceSelect } from "./device-select";
import { MediaComboButton } from "./media-combo-button";
import { MediaToggleButton } from "./media-toggle-button";
import { DesktopHeader } from "../../components/desktop-header";
import { MobileHeader } from "../../components/mobile-header";
import { VideoDisplay } from "./video-display";
import { Button } from "@workspace/ui/components";

type SessionRoomProps = {
  timerSeconds?: number;
  onEndSession?: () => void;
};

// Mock devices
const mockMicrophones = [
  { id: "mic-1", label: "MacBook Microphone (Built-in)" },
  { id: "mic-2", label: "External Microphone" },
  { id: "mic-3", label: "AirPods Pro" },
];

const mockSpeakers = [
  { id: "speaker-1", label: "Macbook Dynamics (Built-In)" },
  { id: "speaker-2", label: "External Speakers" },
  { id: "speaker-3", label: "AirPods Pro" },
];

const mockCameras = [
  { id: "cam-1", label: "Camera MacBook (Built-In)" },
  { id: "cam-2", label: "External Webcam" },
];

export function SessionRoom({
  timerSeconds = 179,
  onEndSession,
}: SessionRoomProps) {
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [isFloatingMode, setIsFloatingMode] = useState(false);

  // Format time as MM:SS
  const minutes = Math.floor(timerSeconds / 60);
  const seconds = timerSeconds % 60;
  const timeString = `${minutes}:${seconds.toString().padStart(2, "0")}`;

  return (
    <div className="w-full h-screen bg-[#f3e4c9] flex flex-col overflow-hidden items-center gap-7 pt-20.5 md:pt-0 md:pb-5">
      <DesktopHeader title="Marcus Challenge" />
      <MobileHeader />

      <div className="flex flex-auto px-5 w-full lg:items-center justify-center">
        <div className="flex flex-col md:rounded-4xl md:border border-[#e3d8c4] md:shadow-lg md:p-5 md:gap-5 md:bg-white w-full md:w-fit md:max-h-full lg:h-fit lg:w-full lg:max-w-[1140px]">
          <div className="grid grid-cols-1 gap-3 h-full md:h-[calc(100%-80px)] lg:grid-cols-2">
            <div className="relative flex items-center justify-center w-full h-full md:aspect-video md:w-none lg:h-none lg:w-full lg:max-w-[560px]">
              <VideoDisplay label="Marcus, CTO" type="avatar" />
            </div>
            <div className="relative flex items-center justify-center w-full h-full md:aspect-video md:w-none lg:h-none lg:w-full lg:max-w-[560px]">
              <VideoDisplay label="You" type="user" />
            </div>
          </div>
          <div className="hidden md:flex w-full justify-center gap-3">
            <MediaComboButton enabled type="audio" onToggle={() => {}}>
              <span>test</span>
            </MediaComboButton>
            <MediaComboButton enabled type="audio" onToggle={() => {}}>
              <span>test</span>
            </MediaComboButton>
            <Button variant="d-default" className="h-12" onClick={onEndSession}>
              End Session
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Fixed Bottom Controls */}
      <div className="md:hidden  pb-[34px]">
        <div className="mx-[20px] bg-white border border-[#e3d8c4] border-solid flex items-center p-[12px] rounded-[20px] shadow-[0px_4px_4px_0px_rgba(41,37,36,0.12)]">
          <div className="flex flex-1 gap-[12px] items-center">
            <MediaToggleButton
              type="audio"
              enabled={audioEnabled}
              onToggle={() => setAudioEnabled(!audioEnabled)}
              size="sm"
            />
            <MediaToggleButton
              type="video"
              enabled={videoEnabled}
              onToggle={() => setVideoEnabled(!videoEnabled)}
              size="sm"
            />
          </div>
          {/* Divider */}
          <div className="h-[24px] w-[1px] bg-[#e3d8c4] mx-[12px]" />
          {/* End Session Button */}
          <button
            onClick={onEndSession}
            className="bg-white border-2 border-[#e3d8c4] border-solid flex items-center justify-center px-[16px] py-[10px] rounded-[12px]"
          >
            <p className="capitalize font-semibold leading-[20px] text-[#292524] text-[16px]">
              End Session
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
